import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Avatar,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Unarchive as UnarchiveIcon,
  Archive as ArchiveIcon,
  Inventory2Outlined as InventoryIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getArchivedClients, unarchiveClient } from '../../services/firebase';
import { Client } from '../../types';
import { formatDate } from '../../utils/formatters';
import { getArchiveReasonText } from '../../utils/archive';

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'country' | 'archivedAt' | 'reason';

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase() || first.toUpperCase();
};

const avatarColorFor = (name: string): string => {
  const palette = ['#1E3A5F', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#0EA5E9', '#14B8A6'];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
};

const getArchivedDate = (c: Client): Date | null =>
  c.archivedAt ? c.archivedAt.toDate() : null;

const ArchivedClients: React.FC = () => {
  const navigate = useNavigate();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('archivedAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getArchivedClients();
        if (cancelled) return;
        setClients(data);
      } catch (err) {
        console.error('ArchivedClients: Error loading archived clients:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRestore = async (clientId: string, name: string) => {
    try {
      await unarchiveClient(clientId);
      setClients((prev) => prev.filter((c) => c.id !== clientId));
      toast.success(`${name} restored`);
    } catch (err) {
      console.error('Error restoring client:', err);
      toast.error('Failed to restore client');
    }
  };

  const visibleClients = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const matches = clients.filter(
      (c) =>
        c.fullName.toLowerCase().includes(lower) ||
        c.email.toLowerCase().includes(lower) ||
        c.phoneNumber.includes(searchTerm) ||
        (typeof c.countryOfOrigin === 'string' &&
          c.countryOfOrigin.toLowerCase().includes(lower)),
    );

    const sorted = [...matches].sort((a, b) => {
      const cmp = (x: Client, y: Client) => {
        switch (orderBy) {
          case 'name':
            return x.fullName.localeCompare(y.fullName);
          case 'country':
            return String(x.countryOfOrigin || '').localeCompare(
              String(y.countryOfOrigin || ''),
            );
          case 'archivedAt': {
            const xd = getArchivedDate(x);
            const yd = getArchivedDate(y);
            if (!xd && !yd) return 0;
            if (!xd) return 1;
            if (!yd) return -1;
            return xd.getTime() - yd.getTime();
          }
          case 'reason':
            return getArchiveReasonText(x.archiveReason || 'archived').localeCompare(
              getArchiveReasonText(y.archiveReason || 'archived'),
            );
          default:
            return 0;
        }
      };
      return order === 'asc' ? cmp(a, b) : cmp(b, a);
    });

    return sorted;
  }, [clients, searchTerm, order, orderBy]);

  const handleSort = (col: OrderBy) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const pageRows = visibleClients.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const stickyFirstHead = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 3,
    backgroundColor: '#F9FAFB',
  };
  const stickyFirstBody = {
    position: { xs: 'sticky' as const, md: 'static' as const },
    left: 0,
    zIndex: 1,
    backgroundColor: 'inherit',
  };

  const TableSkeleton = () => (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i} sx={{ '& td': { height: 56 } }}>
          <TableCell sx={stickyFirstBody}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="text" width={200} height={18} />
            </Box>
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={120} height={16} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={140} />
          </TableCell>
          <TableCell>
            <Skeleton variant="text" width={90} />
          </TableCell>
          <TableCell align="right">
            <Skeleton variant="rounded" width={72} height={28} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const EmptyState: React.FC<{ searched: boolean }> = ({ searched }) => (
    <Box sx={{ textAlign: 'center', py: { xs: 12, md: 16 }, px: 4 }}>
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: '#F3F4F6',
          color: 'text.secondary',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <InventoryIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography
        sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
      >
        {searched ? 'No matching archived clients' : 'No archived clients'}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
        {searched
          ? 'Try adjusting your search.'
          : 'Clients will appear here once they are archived.'}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      <Box
        sx={{
          mb: 6,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 3,
        }}
      >
        <Box>
          <Typography
            component="h1"
            sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
          >
            Archived Clients
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Clients whose cases have been completed or archived
          </Typography>
        </Box>
        <Chip
          icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
          label={`${clients.length} archived`}
          sx={{
            bgcolor: '#F3F4F6',
            color: 'text.secondary',
            fontWeight: 600,
            alignSelf: { xs: 'flex-start', sm: 'auto' },
          }}
        />
      </Box>

      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            p: { xs: 4, sm: 5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search archived clients…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 360 } }}
            inputProps={{ 'aria-label': 'Search archived clients' }}
          />
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720, tableLayout: 'fixed' }} aria-label="Archived clients">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...stickyFirstHead, width: '40%' }}>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '20%' }}>
                  <TableSortLabel
                    active={orderBy === 'country'}
                    direction={orderBy === 'country' ? order : 'asc'}
                    onClick={() => handleSort('country')}
                  >
                    Country
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '20%' }}>
                  <TableSortLabel
                    active={orderBy === 'reason'}
                    direction={orderBy === 'reason' ? order : 'asc'}
                    onClick={() => handleSort('reason')}
                  >
                    Reason
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '14%' }}>
                  <TableSortLabel
                    active={orderBy === 'archivedAt'}
                    direction={orderBy === 'archivedAt' ? order : 'asc'}
                    onClick={() => handleSort('archivedAt')}
                  >
                    Archived On
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 110 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableSkeleton />
              ) : visibleClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ p: 0, borderBottom: 'none' }}>
                    <EmptyState searched={!!searchTerm} />
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((client, idx) => {
                  const initials = initialsFromName(client.fullName);
                  const color = avatarColorFor(client.fullName);
                  const archivedOn = getArchivedDate(client);
                  const reason = getArchiveReasonText(client.archiveReason || 'archived');
                  const rowBg = idx % 2 === 1 ? '#F9FAFB' : '#FFFFFF';
                  return (
                    <TableRow
                      key={client.id}
                      hover
                      sx={{
                        backgroundColor: { xs: '#FFFFFF', md: rowBg },
                        '& td': { height: 56 },
                        '&:hover .row-actions': { opacity: 1 },
                        '@media (hover: none)': {
                          '& .row-actions': { opacity: 1 },
                        },
                      }}
                    >
                      <TableCell sx={stickyFirstBody}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: color,
                              fontSize: '0.75rem',
                              color: '#FFFFFF',
                              opacity: 0.85,
                            }}
                          >
                            {initials}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {client.fullName}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {client.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: 'text.secondary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {client.countryOfOrigin || '—'}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                        {reason}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {archivedOn ? formatDate(archivedOn) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          className="row-actions"
                          sx={{
                            display: 'inline-flex',
                            gap: 0.25,
                            opacity: { xs: 1, md: 0 },
                            transition: 'opacity 150ms ease',
                          }}
                        >
                          <Tooltip title="View" arrow>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/client/${client.id}`)}
                              aria-label={`View ${client.fullName}`}
                            >
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Restore client" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleRestore(client.id, client.fullName)}
                              aria-label={`Restore ${client.fullName}`}
                              sx={{
                                color: 'success.main',
                                '&:hover': { bgcolor: '#ECFDF5' },
                              }}
                            >
                              <UnarchiveIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && visibleClients.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={visibleClients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows:"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${count === 0 ? 0 : from}–${to} of ${count} ${
                count === 1 ? 'client' : 'clients'
              }`
            }
          />
        )}
      </Paper>
    </Box>
  );
};

export default ArchivedClients;
